import type { DraftFieldError, DraftFieldErrorCode } from './api';

/**
 * Maps each machine-readable `DraftFieldError` code to a client-owned i18n key. Field errors carry no
 * copy on the wire (unlike rule findings, which are bilingual) — the web owns the wording, so this
 * table is the single source of truth for their messages. Components call `t(fieldErrorKey(code))`.
 */
export const FIELD_ERROR_KEY: Record<DraftFieldErrorCode, string> = {
  PROFILE_NOT_READY: 'invoices.compose.field_errors.PROFILE_NOT_READY',
  NO_BUYER: 'invoices.compose.field_errors.NO_BUYER',
  NO_LINES: 'invoices.compose.field_errors.NO_LINES',
  LINE_NAME_MISSING: 'invoices.compose.field_errors.LINE_NAME_MISSING',
  LINE_NAME_TOO_LONG: 'invoices.compose.field_errors.LINE_NAME_TOO_LONG',
  LINE_QUANTITY_INVALID: 'invoices.compose.field_errors.LINE_QUANTITY_INVALID',
  LINE_UNIT_PRICE_INVALID: 'invoices.compose.field_errors.LINE_UNIT_PRICE_INVALID',
  LINE_VAT_RATE_INVALID: 'invoices.compose.field_errors.LINE_VAT_RATE_INVALID',
  SALE_DATE_MISSING: 'invoices.compose.field_errors.SALE_DATE_MISSING',
  SALE_DATE_INVALID: 'invoices.compose.field_errors.SALE_DATE_INVALID',
  CURRENCY_INVALID: 'invoices.compose.field_errors.CURRENCY_INVALID',
  PAYMENT_METHOD_INVALID: 'invoices.compose.field_errors.PAYMENT_METHOD_INVALID',
  PAYMENT_DUE_DATE_INVALID: 'invoices.compose.field_errors.PAYMENT_DUE_DATE_INVALID',
  PAYMENT_PAID_DATE_INVALID: 'invoices.compose.field_errors.PAYMENT_PAID_DATE_INVALID',
  PAYMENT_ACCOUNT_INVALID: 'invoices.compose.field_errors.PAYMENT_ACCOUNT_INVALID',
  ADDITIONAL_DATA_KEY_MISSING: 'invoices.compose.field_errors.ADDITIONAL_DATA_KEY_MISSING',
  ADDITIONAL_DATA_VALUE_MISSING: 'invoices.compose.field_errors.ADDITIONAL_DATA_VALUE_MISSING',
  ADDITIONAL_DATA_TOO_LONG: 'invoices.compose.field_errors.ADDITIONAL_DATA_TOO_LONG',
  FOOTER_NOTE_TOO_LONG: 'invoices.compose.field_errors.FOOTER_NOTE_TOO_LONG',
  LINE_UNIT_GROSS_PRICE_INVALID: 'invoices.compose.field_errors.LINE_UNIT_GROSS_PRICE_INVALID',
  MARGIN_PROCEDURE_MISSING: 'invoices.compose.field_errors.MARGIN_PROCEDURE_MISSING',
};

export function fieldErrorKey(code: DraftFieldErrorCode): string {
  return FIELD_ERROR_KEY[code];
}

/** The line-scoped codes (they carry a 1-based `line`); the rest are document-level. */
export const LINE_ERROR_CODES: ReadonlySet<DraftFieldErrorCode> = new Set([
  'LINE_NAME_MISSING',
  'LINE_NAME_TOO_LONG',
  'LINE_QUANTITY_INVALID',
  'LINE_UNIT_PRICE_INVALID',
  'LINE_UNIT_GROSS_PRICE_INVALID',
  'LINE_VAT_RATE_INVALID',
]);

/**
 * Groups line-scoped field errors by *line key* instead of position. `lineKeys` is the line order
 * that produced `fieldErrors` (their 1-based `line` indexes into it). Keying by the stable key — not
 * the index — means a drag-reorder keeps each error on its own row even before the next validation
 * lands (a reorder changes positions, never which line is invalid). Document-level and
 * out-of-range errors are dropped.
 */
export function lineErrorsByKey(
  fieldErrors: DraftFieldError[],
  lineKeys: number[],
): Map<number, DraftFieldErrorCode[]> {
  const byKey = new Map<number, DraftFieldErrorCode[]>();
  for (const e of fieldErrors) {
    if (e.line == null || !LINE_ERROR_CODES.has(e.code)) continue;
    const key = lineKeys[e.line - 1];
    if (key == null) continue;
    const codes = byKey.get(key) ?? [];
    codes.push(e.code);
    byKey.set(key, codes);
  }
  return byKey;
}

/** The row-scoped additional-data codes (they carry a 1-based `line`); `FOOTER_NOTE_TOO_LONG` is document-level. */
export const ADDITIONAL_DATA_ROW_ERROR_CODES: ReadonlySet<DraftFieldErrorCode> = new Set([
  'ADDITIONAL_DATA_KEY_MISSING',
  'ADDITIONAL_DATA_VALUE_MISSING',
  'ADDITIONAL_DATA_TOO_LONG',
]);

/**
 * Groups additional-data field errors by *row key*, the same stable-key scheme as [lineErrorsByKey].
 * `rowKeys` is the additional-data row order that produced `fieldErrors` (their 1-based `line` indexes
 * into it). Document-level and out-of-range errors are dropped.
 */
export function additionalDataErrorsByKey(
  fieldErrors: DraftFieldError[],
  rowKeys: number[],
): Map<number, DraftFieldErrorCode[]> {
  const byKey = new Map<number, DraftFieldErrorCode[]>();
  for (const e of fieldErrors) {
    if (e.line == null || !ADDITIONAL_DATA_ROW_ERROR_CODES.has(e.code)) continue;
    const key = rowKeys[e.line - 1];
    if (key == null) continue;
    const codes = byKey.get(key) ?? [];
    codes.push(e.code);
    byKey.set(key, codes);
  }
  return byKey;
}
